import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { messageFor } from '../lib/errors';
import { ApiRequestError } from '../lib/api-client';
import { transcribeAttachment } from './api';
import { t } from '../lib/i18n';
import { downloadUrl } from '../files/api';
import { MediaViewer, type ViewerItem } from './MediaViewer';

/**
 * The attachments on a message, as the thing they are (context.md §60–§65). Phase 71.
 *
 * A picture shows, a recording plays, everything else is a link. That is the whole rule, and it is
 * worth stating because the alternative — a list of filenames — turns a conversation that is mostly
 * photographs into a conversation that is mostly the word "anexo".
 *
 * Every URL is fetched when the attachment is rendered, not listed with the message: a download
 * link here is a signed credential with a clock on it, and putting one in every row of a thread
 * would mean minting hundreds of them for the two somebody actually opens.
 */

interface StoredMedia {
  fileId?: string;
  filename?: string;
  mimeType?: string;
  /** Set instead of the rest when the file could not be stored (`media_too_large`, …). */
  error?: string;
  /** What the recording says, once somebody has asked for it (Phase 110). */
  transcript?: string;
}

/** What the message row carries, read defensively: it is jsonb written by several providers. */
export function readMedia(media: unknown): StoredMedia[] {
  if (!Array.isArray(media)) return [];

  return media.filter((item): item is StoredMedia => typeof item === 'object' && item !== null);
}

function kindOf(mimeType: string | undefined): 'image' | 'audio' | 'video' | 'file' {
  if (!mimeType) return 'file';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType.startsWith('video/')) return 'video';

  return 'file';
}

export function MessageMedia({
  media,
  outbound = false,
  conversationId,
  messageId,
}: {
  media: unknown;
  /** Both needed to ask for a transcript; absent where a message is drawn outside a thread. */
  conversationId?: string;
  messageId?: string;
  /**
   * Whether the account sent this message (Phase 93).
   *
   * It changes what an unavailable attachment means. WhatsApp will not serve an account its own
   * uploads back, so a file an agent attached on their phone cannot be kept here — which is worth
   * saying in those words rather than as "anexo indisponível", a sentence that reads like a fault.
   */
  outbound?: boolean;
}): React.JSX.Element | null {
  const items = readMedia(media);

  if (items.length === 0) return null;

  return (
    <div className="bubble__media">
      {items.map((item, index) => (
        <Attachment
          key={item.fileId ?? `broken-${index}`}
          item={item}
          outbound={outbound}
          {...(conversationId && messageId
            ? { transcribe: { conversationId, messageId, index } }
            : {})}
        />
      ))}
    </div>
  );
}

function Attachment({
  item,
  outbound,
  transcribe,
}: {
  item: StoredMedia;
  outbound: boolean;
  /** Where this attachment lives, so a transcript can be asked for and kept. */
  transcribe?: { conversationId: string; messageId: string; index: number };
}): React.JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState<'gone' | 'error' | null>(null);
  const [open, setOpen] = useState(false);

  const kind = kindOf(item.mimeType);

  useEffect(() => {
    if (!item.fileId) return;

    let cancelled = false;

    downloadUrl(item.fileId)
      .then((ticket) => {
        if (!cancelled) setUrl(ticket.url);
      })
      .catch((error: unknown) => {
        /*
         * Deleted, and said so (Phase 137).
         *
         * The tenant can remove conversation media to free space, exactly as WhatsApp drops old
         * media from a phone. "Anexo indisponível" reads as a fault in the product; "removido para
         * liberar espaço" reads as something somebody chose, which is what it is.
         */
        if (!cancelled) {
          setFailed(
            error instanceof ApiRequestError && error.code === 'storage.file_removed'
              ? 'gone'
              : 'error',
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [item.fileId]);

  /*
   * An attachment the system could not keep.
   *
   * Said plainly rather than hidden: the message still arrived, and an agent reading it needs to
   * know there was a file they are not seeing rather than wonder why the customer sent nothing.
   */
  if (item.error || !item.fileId) {
    return (
      <span className="bubble__media-missing">
        {outbound ? t('inbox.mediaFromDevice') : t('inbox.mediaUnavailable')}
      </span>
    );
  }

  if (failed) {
    return (
      <span className="bubble__media-missing">
        {t(failed === 'gone' ? 'inbox.mediaRemoved' : 'inbox.mediaUnavailable')}
      </span>
    );
  }

  if (!url) return <span className="bubble__media-loading" aria-hidden="true" />;

  const viewable: ViewerItem = {
    url,
    filename: item.filename ?? t('inbox.attachment'),
    mimeType: item.mimeType ?? 'application/octet-stream',
  };

  /*
   * Opened here rather than in a tab (Phase 91).
   *
   * An agent looking at a photograph is in the middle of answering: a tab switch costs them the
   * thread, the draft and their place in it. The viewer sits over the conversation and Escape puts
   * it back.
   */
  if (kind === 'image') {
    return (
      <>
        <button
          type="button"
          className="bubble__image"
          onClick={() => setOpen(true)}
          aria-label={t('inbox.attachmentOpen')}
        >
          <img src={url} alt={viewable.filename} loading="lazy" />
        </button>

        {open && <MediaViewer item={viewable} onClose={() => setOpen(false)} />}
      </>
    );
  }

  if (kind === 'audio') {
    /*
     * The browser's own player, and the words under it (Phase 110).
     *
     * A desk cannot read a recording: an agent scrolling a thread sees "0:34" where the question
     * was, stops, puts headphones on, and listens — and the colleague who picks the thread up later
     * does it again. The transcript is asked for once and kept, so it is there on the next reload
     * and for whoever comes next.
     */
    return (
      <span className="bubble__voice">
        <audio className="bubble__audio" controls src={url} preload="none" />

        <Transcript item={item} {...(transcribe ? { where: transcribe } : {})} />
      </span>
    );
  }

  if (kind === 'video') {
    return (
      <>
        {/*
          `preload="metadata"` is what draws a first frame instead of a black rectangle, and it is
          the difference between a thread of videos and a thread of identical dark boxes.
        */}
        <button
          type="button"
          className="bubble__video"
          onClick={() => setOpen(true)}
          aria-label={t('inbox.attachmentOpen')}
        >
          <video src={url} preload="metadata" muted playsInline />
          <span className="bubble__play" aria-hidden="true">
            ▶
          </span>
        </button>

        {open && <MediaViewer item={viewable} onClose={() => setOpen(false)} />}
      </>
    );
  }

  return (
    <>
      <button type="button" className="bubble__file" onClick={() => setOpen(true)}>
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path
            d="M14 3v5h5M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
        <span>{viewable.filename}</span>
      </button>

      {open && <MediaViewer item={viewable} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * The words in a recording, or the way to ask for them.
 *
 * The button disappears once there is a transcript: it is a thing you do once, and a control that
 * stays offers to spend money on the same seconds again.
 */
function Transcript({
  item,
  where,
}: {
  item: StoredMedia;
  where?: { conversationId: string; messageId: string; index: number };
}): React.JSX.Element | null {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const ask = useMutation({
    mutationFn: () => transcribeAttachment(where!.conversationId, where!.messageId, where!.index),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['conversation', where!.conversationId] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (item.transcript) {
    return <span className="bubble__transcript">{item.transcript}</span>;
  }

  if (!where) return null;

  return (
    <>
      <button
        type="button"
        className="bubble__transcribe"
        onClick={() => ask.mutate()}
        disabled={ask.isPending}
      >
        {ask.isPending ? t('inbox.transcribing') : t('inbox.transcribe')}
      </button>

      {error && (
        <span className="bubble__failure" role="alert">
          {error}
        </span>
      )}
    </>
  );
}
